import { loadGoogleMaps } from "../utils/load_google_maps"
import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"
import { position, recordedJourney, sampleAt, isTrackingGap } from "../utils/tracking_playback"

const trackingLabels = { live: "Live", delayed: "GPS delayed", waiting: "Waiting for GPS", stopped: "Tracking stopped", not_started: "No tracking" }
const runLabels = { planned: "Planned", departed: "On the road", ended: "Ended", completed: "Completed" }
const outcomeSymbols = { done: "✓", partial: "◐", pending: "○", exception: "!" }

export default class extends Controller {
  static targets = ["map", "list", "status", "refresh", "mapStatus", "count", "search", "layout", "viewButton", "selectionTitle", "selectionDetail", "execution", "overview", "playback", "playbackControls", "playbackDetails", "playbackNotice", "reloadHistory", "timeline", "clock", "play", "restart", "speed"]
  static values = { url: String, history: String, timeZone: String }

  connect() {
        loadGoogleMaps().catch(() => {})
    this.disconnected = false
    this.loading = false
    this.fitted = false
    this.changeTimer = null
    this.historyVersion = (this.historyVersion || 0) + 1
    this.connectionVersion = (this.connectionVersion || 0) + 1
    this.map = null
    this.pins = new Map()
    this.stopPins = new Map()
    this.lines = []
    this.runs = []
    this.selectedId = null
    this.journey = recordedJourney([])
    this.renderSelection()
    this.statusTarget.textContent = "Loading runs…"
    this.mapReady = () => this.initializeMap()
    this.mapError = () => { this.mapStatusTarget.hidden = false; this.mapStatusTarget.textContent = "Map unavailable. Check the organisation’s Google Maps setup." }
    this.visibility = () => { if (document.hidden) this.pause(); else this.refresh() }
    window.addEventListener("google-maps:ready", this.mapReady)
    window.addEventListener("google-maps:error", this.mapError)
    document.addEventListener("visibilitychange", this.visibility)
    this.initializeMap()
    this.refresh()
    this.mapTimeout = setTimeout(() => { if (!this.map) this.mapError() }, 12000)
    this.timer = setInterval(() => { if (!document.hidden) this.refresh() }, 15000)
    this.subscription = consumer.subscriptions.create("DeliveryTrackingChannel", { received: () => {
      if (!this.changeTimer) this.changeTimer = setTimeout(() => { this.changeTimer = null; if (!document.hidden) this.refresh() }, 1500)
    } })
  }

  disconnect() {
    this.disconnected = true
    this.pause()
    clearInterval(this.timer)
    clearTimeout(this.mapTimeout)
    clearTimeout(this.changeTimer)
    this.abort?.abort()
    this.historyAbort?.abort()
    this.subscription?.unsubscribe()
    window.removeEventListener("google-maps:ready", this.mapReady)
    window.removeEventListener("google-maps:error", this.mapError)
    document.removeEventListener("visibilitychange", this.visibility)
    this.pins.forEach(pin => pin.setMap(null))
    this.stopPins.forEach(pin => pin.setMap(null))
    this.closeStopInfo()
    this.stopInfo = null
    this.clearJourneyMap()
  }

  initializeMap() {
    if (this.map || !window.google?.maps?.Map || window.googleMapsAuthFailed) return
    this.map = new google.maps.Map(this.mapTarget, { center: { lat: -33.87, lng: 151.21 }, zoom: 10, mapTypeControl: false, streetViewControl: false, fullscreenControl: false })
    this.renderPins()
    if (this.selectedId) {
      this.drawJourney()
      this.fitMap()
    }
  }

  async fetchJSON(url, signal) {
    const response = await fetch(url, { headers: { Accept: "application/json" }, signal })
    if (!response.ok) throw new Error(response.status === 403 || response.status === 401 ? "Access changed. Sign in again or check your delivery permissions." : "Tracking data is unavailable. Please retry.")
    return response.json()
  }

  async refresh() {
    if (this.loading || this.disconnected) return
    this.loading = true
    this.refreshTarget.disabled = true
    const abort = this.abort = new AbortController()
    const version = this.connectionVersion
    const timeout = setTimeout(() => abort.abort(), 20000)
    try {
      const data = await this.fetchJSON(this.urlValue, abort.signal)
      if (this.disconnected || version !== this.connectionVersion) return
      this.runs = data.runs
      this.statusTarget.textContent = `Updated ${this.time(data.server_time)}`
      if (this.selectedId && !this.runs.some(run => run.id === this.selectedId)) this.overview()
      this.renderList()
      this.renderPins()
      this.renderSelection()
      this.renderStopInfo()
    } catch {
      if (!this.disconnected && version === this.connectionVersion && !this.runs.length) {
        this.statusTarget.textContent = "Waiting for updates…"
      }
    } finally {
      clearTimeout(timeout)
      if (!this.disconnected && version === this.connectionVersion) {
        this.loading = false
        this.refreshTarget.disabled = false
      }
    }
  }

  changeDate(event) { if (event.target.value && event.target.checkValidity()) event.target.form.requestSubmit() }
  searchChanged() {
    this.closeStopInfo()
    if (this.selectedId && !this.visibleRuns().some(run => run.id === this.selectedId)) this.overview()
    this.renderList()
    this.renderPins()
  }
  visibleRuns() {
    const query = this.searchTarget.value.trim().toLowerCase()
    return this.runs.filter(run => [run.name, run.driver, run.vehicle].some(value => String(value || "").toLowerCase().includes(query)))
  }
  switchView(event) { this.setView(event.currentTarget.dataset.view) }
  setView(view) {
    this.layoutTarget.dataset.mobileView = view
    this.viewButtonTargets.forEach(button => {
      button.classList.toggle("active", button.dataset.view === view)
      button.setAttribute("aria-pressed", String(button.dataset.view === view))
    })
    if (this.map) google.maps.event.trigger(this.map, "resize")
  }

  renderList() {
    const focused = this.listTarget.contains(document.activeElement) ? document.activeElement.dataset.runId : null
    const scroll = this.listTarget.scrollTop
    this.listTarget.replaceChildren()
    const runs = this.visibleRuns()
    const rank = run => run.status === "departed" ? 0 : run.status === "planned" ? 1 : 2
    runs.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name) || a.id.localeCompare(b.id, "en", { numeric: true }))
    for (const run of runs) {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "delivery-tracking-run"
      button.dataset.runId = run.id
      button.title = `Captured ${this.time(run.captured_at, true)} · Uploaded ${this.time(run.received_at, true)}`
      button.setAttribute("aria-pressed", String(this.selectedId === run.id))
      const heading = this.elementWithText("span", "delivery-tracking-run-title", "")
      heading.append(this.elementWithText("strong", "", `${run.name} · #${run.id}`), this.elementWithText("span", `delivery-tracking-state is-${run.tracking}`, trackingLabels[run.tracking] || run.tracking))
      button.append(heading,
        this.elementWithText("span", "delivery-tracking-run-detail", `${run.driver || "No driver"} · ${run.vehicle || "No vehicle"}`),
        this.elementWithText("span", "delivery-tracking-run-detail", `${runLabels[run.status] || run.status} · ${run.shipment_count || 0} shipments · ${run.captured_at ? `GPS ${this.time(run.captured_at)}` : "No GPS yet"}`))
      if (run.progress?.total) {
        const completion = this.elementWithText("span", "delivery-tracking-completion", "")
        completion.append(this.elementWithText("span", "delivery-tracking-outcome is-done", `✓ ${run.progress.done}/${run.progress.total} done`))
        if (run.progress.pending) completion.append(this.elementWithText("span", "fg-2", `${run.progress.pending} pending`))
        if (run.progress.exceptions) completion.append(this.elementWithText("span", "delivery-tracking-outcome is-exception", `${run.progress.exceptions} exception${run.progress.exceptions === 1 ? "" : "s"}`))
        const bar = document.createElement("progress")
        bar.max = run.progress.total
        bar.value = run.progress.done
        bar.setAttribute("aria-label", `${run.progress.done} of ${run.progress.total} shipments done`)
        completion.append(bar)
        button.append(completion)
      }
      button.addEventListener("click", () => this.selectRun(run))
      this.listTarget.append(button)
      if (focused === run.id) button.focus({ preventScroll: true })
    }
    this.listTarget.scrollTop = scroll
    this.countTarget.textContent = `${runs.length} of ${this.runs.length} runs`
    if (!runs.length) this.listTarget.append(this.elementWithText("p", "p-3 small fg-2", this.runs.length ? "No runs match this search." : "No runs for this date. Choose another day."))
  }
  elementWithText(tag, className, text) {
    const node = document.createElement(tag)
    node.className = className
    node.textContent = text
    return node
  }
  time(value, seconds = false) {
    if (!value) return "—"
    return new Date(value).toLocaleTimeString([], { timeZone: this.timeZoneValue, hour: "2-digit", minute: "2-digit", ...(seconds ? { second: "2-digit" } : {}) })
  }
  dateTime(value) {
    return new Date(value).toLocaleString([], { timeZone: this.timeZoneValue, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
  }
  markerIcon(color, scale = 9) {
    const size = scale * 2 + 4
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><circle cx="${size / 2}" cy="${size / 2}" r="${scale}" fill="${color}" stroke="white" stroke-width="2"/></svg>`
    return { url: `data:image/svg+xml,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(size, size), anchor: new google.maps.Point(size / 2, size / 2), labelOrigin: new google.maps.Point(size / 2, size + 9) }
  }
  stopIcon(color, state, number) {
    const routeColor = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color || "") ? color : "#64748b"
    const status = ["done", "partial", "exception"].includes(state) ? state : "pending"
    const ink = { done: "#21804a", partial: "#a66b10", exception: "#b83f3a", pending: "#64748b" }[status]
    const text = Number.isSafeInteger(number) && number > 0 ? String(number) : "?"
    const centerRadius = Math.max(15, text.length * 4 + 4), radius = centerRadius + 5
    const x = radius + 9, y = radius + 9, tip = y + radius + 12
    const width = x * 2, height = tip + 11
    const shape = `M${x} ${tip}C${x - 6} ${tip - 10} 9 ${y + 10} 9 ${y}A${radius} ${radius} 0 1 1 ${x + radius} ${y}C${x + radius} ${y + 10} ${x + 6} ${tip - 10} ${x} ${tip}Z`
    // Keep the number on white, the pin in the run colour, and status on the outline.
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <defs><filter id="stop-shadow" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-color="#0f172a" flood-opacity=".24"/></filter></defs>
      <g filter="url(#stop-shadow)">
        <path id="stop-status" data-state="${status}" d="${shape}" fill="${routeColor}" stroke="${ink}" stroke-width="7" stroke-linejoin="round"/>
        <path d="${shape}" fill="${routeColor}" stroke="white" stroke-width="3" stroke-linejoin="round" paint-order="stroke fill"/>
        <circle id="stop-center" cx="${x}" cy="${y}" r="${centerRadius}" fill="white"/>
        <text id="stop-number" x="${x}" y="${y + 4.5}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${text.length <= 2 ? 14 : 12}" font-weight="700" fill="#253343">${text}</text>
      </g>
    </svg>`
    return { url: `data:image/svg+xml,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(width, height), anchor: new google.maps.Point(x, tip) }
  }
  completionSummary(progress) {
    if (!progress) return ""
    return [`${progress.done}/${progress.total} done`, ...(progress.pending ? [`${progress.pending} pending`] : []), ...(progress.exceptions ? [`${progress.exceptions} exception${progress.exceptions === 1 ? "" : "s"}`] : [])].join(" · ")
  }
  // Keep completed and pending shipments visible within the selected run.
  mappedRuns() { return this.selectedId ? this.runs.filter(run => run.id === this.selectedId) : this.runs }
  renderStops() {
    const ids = new Set()
    for (const run of this.mappedRuns()) {
      for (const stop of run.stops || []) {
        const coordinate = position(stop.position)
        if (!coordinate) continue
        const id = JSON.stringify([run.id, stop.key])
        ids.add(id)
        let pin = this.stopPins.get(id)
        if (!pin) {
          pin = new google.maps.Marker({ map: this.map, position: coordinate })
          pin.addListener("click", () => this.showStop(run.id, stop.key))
          this.stopPins.set(id, pin)
        }
        pin.setPosition(coordinate)
        pin.setIcon(this.stopIcon(run.color, stop.state, stop.number))
        pin.setZIndex(run.id === this.selectedId ? 20 : 10)
        pin.setLabel(null)
        pin.setTitle(`${run.name} · Stop ${stop.number} · ${stop.customer} · ${this.completionSummary(stop.progress)}`)
      }
    }
    this.stopPins.forEach((pin, id) => { if (!ids.has(id)) { pin.setMap(null); this.stopPins.delete(id) } })
  }
  showStop(runId, key) {
    const run = this.runs.find(candidate => candidate.id === runId)
    const stop = run?.stops?.find(candidate => candidate.key === key)
    if (!stop) return
    if (this.selectedId !== runId) this.selectRun(run)
    this.openStop = { runId, key }
    this.stopInfo ||= new google.maps.InfoWindow()
    this.renderStopInfo()
    this.stopInfo.open({ map: this.map, anchor: this.stopPins.get(JSON.stringify([runId, key])) })
  }
  closeStopInfo() { this.stopInfo?.close(); this.openStop = null }
  renderStopInfo() {
    if (!this.openStop) return
    const { runId, key } = this.openStop
    const run = this.runs.find(candidate => candidate.id === runId)
    const stop = run?.stops?.find(candidate => candidate.key === key)
    if (!stop || !this.stopPins.has(JSON.stringify([runId, key]))) { this.closeStopInfo(); return }
    const content = this.elementWithText("div", "delivery-tracking-stop-info", "")
    content.append(this.elementWithText("strong", "", `${run.name} · Stop ${stop.number}`),
      this.elementWithText("div", "mt-1", [stop.customer, stop.location].filter(Boolean).join(" · ")),
      this.elementWithText("div", "small fg-2 mb-2", stop.address || "Address unavailable"),
      this.elementWithText("strong", "d-block small mb-2", this.completionSummary(stop.progress)))
    for (const shipment of stop.shipments) {
      const row = this.elementWithText("div", "delivery-tracking-shipment small", "")
      row.append(this.elementWithText("span", "", shipment.number),
        this.elementWithText("span", `delivery-tracking-outcome is-${shipment.state}`, `${outcomeSymbols[shipment.state] || "○"} ${shipment.status}`))
      content.append(row)
    }
    this.stopInfo.setContent(content)
  }
  renderMapStatus() {
    if (!this.map) return
    if (window.googleMapsAuthFailed) { this.mapError(); return }
    const stops = this.mappedRuns().flatMap(run => run.stops || [])
    const mapped = stops.filter(stop => position(stop.position)).length
    const missing = stops.length - mapped
    const detail = []
    const total = stops.reduce((count, stop) => count + (stop.progress?.total || 0), 0)
    const done = stops.reduce((count, stop) => count + (stop.progress?.done || 0), 0)
    if (!this.selectedId) {
      detail.push(`${mapped} shipment stop${mapped === 1 ? "" : "s"}`)
      if (total) detail.push(`${done}/${total} shipments done`)
      if (this.pins.size) detail.push(`${this.pins.size} driver position${this.pins.size === 1 ? "" : "s"}`)
    }
    if (missing) detail.push(`${missing} stop${missing === 1 ? "" : "s"} missing coordinates`)
    if (!mapped && !this.pins.size && !missing && !this.journey.points.length) detail.push("No mapped positions yet")
    this.mapStatusTarget.textContent = detail.join(" · ")
    this.mapStatusTarget.hidden = !detail.length
  }
  renderPins() {
    if (!this.map) return
    const runs = this.selectedId ? [] : this.visibleRuns(), ids = new Set()
    for (const run of runs) {
      const coordinate = position(run.position)
      if (!coordinate) continue
      ids.add(run.id)
      let pin = this.pins.get(run.id)
      if (!pin) {
        pin = new google.maps.Marker({ map: this.map, position: coordinate })
        pin.addListener("click", () => this.selectRun(this.runs.find(candidate => candidate.id === run.id)))
        this.pins.set(run.id, pin)
      }
      pin.setPosition(coordinate)
      pin.setTitle(`${run.name} · ${run.driver || "No driver"} · ${trackingLabels[run.tracking]}`)
      pin.setIcon(this.markerIcon(run.tracking === "live" ? "#13795b" : run.tracking === "stopped" ? "#6b7280" : "#b77b20"))
      pin.setLabel({ text: run.name, color: "#20252b", fontSize: "12px", fontWeight: "600", className: "delivery-tracking-map-label" })
    }
    this.pins.forEach((pin, id) => { if (!ids.has(id)) { pin.setMap(null); this.pins.delete(id) } })
    this.renderStops()
    this.renderMapStatus()
    if (!this.selectedId) {
      if (!this.fitted && (ids.size || this.stopPins.size)) { this.fitMap(); this.fitted = true }
    }
  }
  fitMap() {
    if (!this.map) return
    const coordinates = this.mappedRuns().flatMap(run => (run.stops || []).flatMap(stop => position(stop.position) ? [position(stop.position)] : []))
    coordinates.push(...(this.selectedId ? this.journey.points.map(point => point.position) : this.visibleRuns().flatMap(run => position(run.position) ? [position(run.position)] : [])))
    const positions = [...new Map(coordinates.map(point => [`${point.lat},${point.lng}`, point])).values()]
    if (!positions.length) return
    if (positions.length === 1) { this.map.setCenter(positions[0]); this.map.setZoom(15); return }
    const bounds = new google.maps.LatLngBounds()
    positions.forEach(point => bounds.extend(point))
    this.map.fitBounds(bounds, 50)
  }

  selectRun(run) {
    if (!run) return
    this.closeStopInfo()
    this.selectedId = run.id
    this.renderList()
    this.renderPins()
    this.renderSelection()
    this.setView("map")
    this.loadHistory()
  }
  renderSelection() {
    const run = this.runs.find(candidate => candidate.id === this.selectedId)
    this.overviewTarget.hidden = !run
    this.executionTarget.hidden = !run
    this.playbackTarget.hidden = !run
    this.selectionTitleTarget.textContent = run ? `${run.name} · #${run.id}` : "All runs"
    this.selectionDetailTarget.textContent = run ? `${run.driver || "No driver"} · ${run.vehicle || "No vehicle"} · ${runLabels[run.status]} · ${trackingLabels[run.tracking]}` : "Shipment stops and driver positions. Select a run to view its journey."
    if (run) this.executionTarget.href = run.execution_url
  }
  overview() {
    this.pause()
    this.closeStopInfo()
    this.historyAbort?.abort()
    this.historyVersion = (this.historyVersion || 0) + 1
    this.selectedId = null
    this.journey = recordedJourney([])
    this.clearJourneyMap()
    this.renderSelection()
    this.renderList()
    this.renderPins()
    this.fitMap()
  }
  reloadHistory() { if (this.selectedId) this.loadHistory() }
  closePlaybackOptions() {
    this.playbackDetailsTarget.open = false
    this.playbackDetailsTarget.querySelector("summary").focus()
  }
  dismissPlaybackOptions(event) {
    if (!this.playbackDetailsTarget.contains(event.target)) this.playbackDetailsTarget.open = false
  }
  playbackNotice(message) {
    if (this.playbackNoticeTarget.textContent !== message) this.playbackNoticeTarget.textContent = message
    this.playbackNoticeTarget.hidden = !message
  }
  async loadHistory() {
    this.pause()
    this.historyAbort?.abort()
    const abort = this.historyAbort = new AbortController()
    const version = this.historyVersion = (this.historyVersion || 0) + 1
    const runId = this.selectedId
    this.journey = recordedJourney([])
    this.clearJourneyMap()
    this.playbackControlsTarget.hidden = true
    this.playbackDetailsTarget.open = false
    this.playbackNotice("Loading journey…")
    this.playTarget.disabled = this.restartTarget.disabled = this.timelineTarget.disabled = true
    this.reloadHistoryTarget.disabled = true
    this.timelineTarget.value = "0"
    this.clockTarget.textContent = "—"
    this.timelineTarget.removeAttribute("title")
    this.renderMapStatus()
    this.fitMap()
    const rows = []
    try {
      let cursor = null, throughId = null
      do {
        const url = new URL(`${this.historyValue}/${encodeURIComponent(runId)}.json`, window.location.origin)
        if (cursor) Object.entries(cursor).forEach(([key, value]) => url.searchParams.set(key, value))
        if (throughId != null) url.searchParams.set("through_id", throughId)
        const timeout = setTimeout(() => abort.abort(), 20000)
        let data
        try { data = await this.fetchJSON(url, abort.signal) } finally { clearTimeout(timeout) }
        if (this.disconnected || version !== this.historyVersion) return
        rows.push(...data.points)
        throughId = data.through_id
        if (data.next_cursor && JSON.stringify(cursor) === JSON.stringify(data.next_cursor)) throw new Error("History could not be paged. Reload this journey.")
        cursor = data.next_cursor
      } while (cursor)
      this.journey = recordedJourney(rows)
      this.renderMapStatus()
      const points = this.journey.points
      if (!points.length) {
        this.playbackNotice("No recorded journey for this run")
        return
      }
      this.playbackControlsTarget.hidden = false
      this.timelineTarget.min = String(points[0].time)
      this.timelineTarget.max = String(points.at(-1).time)
      this.timelineTarget.disabled = points.length < 2
      this.restartTarget.disabled = false
      this.playTarget.disabled = points.at(-1).time <= points[0].time
      this.speedTarget.disabled = this.playTarget.disabled
      this.timelineTarget.title = `${this.dateTime(points[0].time)} – ${this.dateTime(points.at(-1).time)} · ${this.timeZoneValue}`
      this.cursor = points[0].time
      this.drawJourney()
      this.renderPlayback()
      this.fitMap()
    } catch (error) {
      if (this.disconnected || version !== this.historyVersion) return
      this.playbackNotice(error.name === "AbortError" ? "Journey timed out. Reload from playback options." : `${error.message} Reload from playback options.`)
    } finally {
      if (!this.disconnected && version === this.historyVersion) this.reloadHistoryTarget.disabled = false
    }
  }
  clearJourneyMap() {
    this.renderedIndex = null
    this.lines.forEach(line => line.setMap(null))
    this.lines = []
    this.playbackPin?.setMap(null)
    this.playbackPin = null
  }
  drawJourney() {
    this.clearJourneyMap()
    if (!this.map || !this.journey.points.length) return
    for (const indexes of this.journey.segments) {
      const path = indexes.map(index => this.journey.points[index].position)
      const full = new google.maps.Polyline({ map: this.map, path, strokeColor: "#879397", strokeOpacity: 0.55, strokeWeight: 4 })
      const played = new google.maps.Polyline({ map: this.map, path: [], strokeColor: "#13795b", strokeWeight: 5 })
      played.playbackIndexes = indexes
      this.lines.push(full, played)
    }
    this.playbackPin = new google.maps.Marker({ map: this.map, position: this.journey.points[0].position, icon: this.markerIcon("#126752", 11), title: "Recorded driver position" })
    this.renderPlayback()
  }
  renderPlayback() {
    const points = this.journey.points, index = sampleAt(points, this.cursor)
    if (index < 0) return
    const point = points[index]
    this.timelineTarget.value = String(Math.floor(this.cursor))
    window.bootstrap.Range.getInstance(this.timelineTarget.closest(".form-range"))?.update()
    this.timelineTarget.setAttribute("aria-valuetext", this.dateTime(this.cursor))
    this.clockTarget.textContent = this.time(this.cursor, true)
    const gap = isTrackingGap(points, index, this.cursor)
    this.playbackNotice(gap ? `No GPS updates in this gap · next at ${this.time(points[index + 1].time)}.` : points.length < 2 ? "Only one recorded position is available." : "")
    if (index !== this.renderedIndex) {
      this.playbackPin?.setPosition(point.position)
      this.playbackPin?.setTitle(`Recorded at ${this.time(point.time, true)}`)
      this.lines.forEach(line => { if (line.playbackIndexes) line.setPath(line.playbackIndexes.filter(i => i <= index).map(i => points[i].position)) })
      this.renderedIndex = index
    }
  }
  seek() { this.pause(); this.cursor = Number(this.timelineTarget.value); this.renderPlayback() }
  restart() { this.pause(); this.cursor = this.journey.points[0]?.time; this.renderedIndex = null; this.renderPlayback(); this.closePlaybackOptions() }
  changeSpeed() { this.lastFrame = null }
  togglePlay() {
    if (this.playing) { this.pause(); return }
    const points = this.journey.points
    if (points.length < 2) return
    if (this.cursor >= points.at(-1).time) this.cursor = points[0].time
    this.playing = true
    this.playTarget.textContent = "Pause"
    this.lastFrame = null
    const tick = now => {
      if (!this.playing || this.disconnected) return
      if (this.lastFrame != null) this.cursor = Math.min(points.at(-1).time, this.cursor + (now - this.lastFrame) * Number(this.speedTarget.value))
      this.lastFrame = now
      this.renderPlayback()
      if (this.cursor >= points.at(-1).time) this.pause()
      else this.frame = requestAnimationFrame(tick)
    }
    this.frame = requestAnimationFrame(tick)
  }
  pause() {
    this.playing = false
    cancelAnimationFrame(this.frame)
    if (this.hasPlayTarget) this.playTarget.textContent = "Play"
  }
}
